// web/src/services/firestore/pmService.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  arrayUnion,
  runTransaction
} from 'firebase/firestore'

import { db } from '@/firebase'

function lc(s) {
  return (s || '').toString().trim().toLowerCase()
}

function readSession() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null')
  } catch {
    return null
  }
}

function chunk(arr, size = 10) {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

function canAccessByCommunity(pm, communitySet) {
  const code = lc(pm?.communityCode)
  const name = lc(pm?.communityName)
  const legacy = lc(pm?.community)
  const communityId = lc(pm?.communityId)
  return (
    communitySet.has(code) ||
    communitySet.has(name) ||
    communitySet.has(legacy) ||
    communitySet.has(communityId)
  )
}

function pmTemplatesCol() {
  return collection(db, 'pmTemplates')
}

function pmTemplateDoc(id) {
  return doc(db, 'pmTemplates', id)
}

function pmInstancesCol() {
  return collection(db, 'pmInstances')
}

function pmInstanceDoc(id) {
  return doc(db, 'pmInstances', id)
}

/* ---------- PM NUMBER HELPERS ---------- */

function padLeft(num, width = 4) {
  const s = String(num)
  return s.length >= width ? s : '0'.repeat(width - s.length) + s
}

/**
 * Generate the next PM number for a given community code.
 * Format: PM-<COMMUNITYCODE>-0001
 *
 * Uses a counters doc:
 *   counters / pm-<COMMUNITYCODE>
 * with a `last` numeric field.
 */
async function getNextPmNumberFS(communityCodeRaw) {
  const baseCode = (communityCodeRaw || 'GEN').toString().trim().toUpperCase()
  const counterId = `pm-${baseCode}`
  const counterRef = doc(db, 'pmCounters', counterId)

  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef)
    const data = snap.exists() ? snap.data() : {}
    const last = typeof data.last === 'number' ? data.last : 0
    const newVal = last + 1

    tx.set(
      counterRef,
      {
        last: newVal,
        updatedAt: new Date()
      },
      { merge: true }
    )

    return newVal
  })

  return `PM-${baseCode}-${padLeft(next, 4)}`
}

/* ---------- Helpers: normalization ---------- */

// Normalize Entrata sync block so Firestore always has a consistent shape
function normalizeEntrataSync(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}

  return {
    // if you never set this yet, default to false
    enabled: !!src.enabled,
    // 'pending' | 'success' | 'error' | null
    lastStatus: src.lastStatus || null,
    // you can store either a Date or ISO string; scheduler can decide
    lastRunAt: src.lastRunAt || null,
    // last error message (if any)
    lastError: src.lastError || null
  }
}

// Normalize template payload before writes (create/update)
function buildPmTemplatePayload(base, { isNew }) {
  const now = new Date()

  const workOrderDefaults =
    base.workOrderDefaults && typeof base.workOrderDefaults === 'object'
      ? base.workOrderDefaults
      : {}

  const hasEntrataSyncKey =
    Object.prototype.hasOwnProperty.call(base, 'entrataSync')

  const payload = {
    ...base,
    workOrderDefaults,
    // ensure isActive has a sensible default
    isActive: base.isActive ?? true
  }

  // Only normalize entrataSync if caller cares about it or for new docs
  if (isNew || hasEntrataSyncKey) {
    payload.entrataSync = normalizeEntrataSync(base.entrataSync)
  }

  if (isNew) {
    payload.createdAt = now
  }
  payload.updatedAt = now

  return payload
}

/* ---------- PM TEMPLATES ---------- */

export async function createPmTemplateFS(data) {
  const base = data || {}

  // Choose a community code to base the PM number on:
  //  - prefer explicit communityCode
  //  - else communityId
  //  - else 'GEN'
  const communityCode =
    base.communityCode ||
    base.communityId ||
    (base.community && (base.community.code || base.community.id)) ||
    'GEN'

  // If caller did not supply pmNumber, generate one
  const pmNumber =
    base.pmNumber || (await getNextPmNumberFS(communityCode))

  // Build final saved payload
  const payload = buildPmTemplatePayload(
    { ...base, pmNumber },
    { isNew: true }
  )

  // Use PM number as the Firestore document ID
  const ref = pmTemplateDoc(pmNumber)
  await setDoc(ref, payload)

  return { id: pmNumber, ...payload }
}

export async function updatePmTemplateFS(id, patch) {
  const payload = buildPmTemplatePayload(patch || {}, { isNew: false })
  const ref = pmTemplateDoc(id)
  await updateDoc(ref, payload)
}

// Read single template
export async function getPmTemplateFS(id) {
  const snap = await getDoc(pmTemplateDoc(id))
  if (!snap.exists()) return null
  const data = snap.data() || {}

  // Defensive: normalize on read so UI can rely on shape, even for older docs
  const normalized = {
    ...data,
    workOrderDefaults:
      data.workOrderDefaults && typeof data.workOrderDefaults === 'object'
        ? data.workOrderDefaults
        : {},
    entrataSync: normalizeEntrataSync(data.entrataSync)
  }

  return { id: snap.id, ...normalized }
}

// List templates by community
export async function listPmTemplatesByCommunityFS(communityId) {
  const qRef = query(
    pmTemplatesCol(),
    where('communityId', '==', communityId),
    orderBy('name', 'asc')
  )
  const snap = await getDocs(qRef)
  return snap.docs.map(d => {
    const data = d.data() || {}
    return {
      id: d.id,
      ...data,
      workOrderDefaults:
        data.workOrderDefaults && typeof data.workOrderDefaults === 'object'
          ? data.workOrderDefaults
          : {},
      entrataSync: normalizeEntrataSync(data.entrataSync)
    }
  })
}

// List all templates (for "All Communities" filter)
export async function listPmTemplatesAllFS() {
  const session = readSession()
  const adminLevel = lc(session?.adminLevel)
  const isAdmin = adminLevel === 'admin' || adminLevel === 'sysconfig'
  const hasAllCommunities = session?.allCommunities === true
  const myCommunities = new Set(
    (Array.isArray(session?.communities) ? session.communities : [])
      .filter(Boolean)
      .map(lc)
  )

  if (isAdmin || hasAllCommunities) {
    const qRef = query(pmTemplatesCol(), orderBy('name', 'asc'))
    const snap = await getDocs(qRef)
    return snap.docs.map(d => {
      const data = d.data() || {}
      return {
        id: d.id,
        ...data,
        workOrderDefaults:
          data.workOrderDefaults && typeof data.workOrderDefaults === 'object'
            ? data.workOrderDefaults
            : {},
        entrataSync: normalizeEntrataSync(data.entrataSync)
      }
    })
  }

  if (!myCommunities.size) return []

  const scopedCodes = Array.from(myCommunities).filter(Boolean)
  const docs = []

  for (const batch of chunk(scopedCodes, 10)) {
    const [byCode, byName, byId, byLegacy] = await Promise.all([
      getDocs(query(pmTemplatesCol(), where('communityCode', 'in', batch))),
      getDocs(query(pmTemplatesCol(), where('communityName', 'in', batch))),
      getDocs(query(pmTemplatesCol(), where('communityId', 'in', batch))),
      getDocs(query(pmTemplatesCol(), where('community', 'in', batch)))
    ])
    docs.push(...byCode.docs, ...byName.docs, ...byId.docs, ...byLegacy.docs)
  }

  const deduped = Array.from(new Map(docs.map(d => [d.id, d])).values())
  const rows = deduped.map(d => {
    const data = d.data() || {}
    return {
      id: d.id,
      ...data,
      workOrderDefaults:
        data.workOrderDefaults && typeof data.workOrderDefaults === 'object'
          ? data.workOrderDefaults
          : {},
      entrataSync: normalizeEntrataSync(data.entrataSync)
    }
  })

  return rows
    .filter(pm => canAccessByCommunity(pm, myCommunities))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}

/* ---------- PM TEMPLATES: Entrata sync helpers ---------- */

// For your daily scheduler: list active PM templates that want Entrata sync
export async function listPmTemplatesForEntrataSyncFS() {
  const qRef = query(
    pmTemplatesCol(),
    where('isActive', '==', true),
    where('entrataSync.enabled', '==', true)
  )

  const snap = await getDocs(qRef)
  return snap.docs.map(d => {
    const data = d.data() || {}
    return {
      id: d.id,
      ...data,
      workOrderDefaults:
        data.workOrderDefaults && typeof data.workOrderDefaults === 'object'
          ? data.workOrderDefaults
          : {},
      entrataSync: normalizeEntrataSync(data.entrataSync)
    }
  })
}

// After scheduler attempts an Entrata sync for a template
// status: 'success' | 'error' | 'pending'
// errorMessage: optional string
export async function markPmTemplateEntrataSyncResultFS(
  id,
  { status, errorMessage }
) {
  const now = new Date()
  const ref = pmTemplateDoc(id)
  await updateDoc(ref, {
    'entrataSync.lastStatus': status || null,
    'entrataSync.lastRunAt': now,
    'entrataSync.lastError': errorMessage || null,
    updatedAt: now
  })
}

/* ---------- PM INSTANCES (for later calendar / Gantt use) ---------- */

export async function listPmInstancesByCommunityFS(
  communityId,
  startISO,
  endISO
) {
  const qRef = query(
    pmInstancesCol(),
    where('communityId', '==', communityId),
    where('scheduledDate', '>=', startISO),
    where('scheduledDate', '<=', endISO),
    orderBy('scheduledDate', 'asc')
  )
  const snap = await getDocs(qRef)
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

export async function markPmInstanceCompletedFS(id, { notes, files }) {
  const now = new Date()
  await updateDoc(pmInstanceDoc(id), {
    status: 'completed',
    completedAt: now,
    notes: notes ?? '',
    files: files ?? [],
    updatedAt: now
  })
}

// When Entrata work order is created for a specific PM instance
export async function savePmInstanceExternalWorkOrderFS(
  id,
  { externalWorkOrderId, payloadSnapshot }
) {
  await updateDoc(pmInstanceDoc(id), {
    externalWorkOrderId,
    externalPayloadSnapshot: payloadSnapshot || null,
    updatedAt: new Date()
  })
}

// When Entrata call fails for a specific PM instance
export async function savePmInstanceErrorFS(id, error) {
  const ref = pmInstanceDoc(id)
  await updateDoc(ref, {
    externalErrors: arrayUnion({
      at: new Date(),
      ...error
    }),
    updatedAt: new Date()
  })
}
